/**
 * Alert detail — where triage happens (Stories 8, 14, 16, 17).
 *
 * The route shell: the selected Client, the alert's query, its loading and
 * not-found states, and the page navigation. Everything else is
 * `AlertPanel`, which the Tickets app's ticket screen embeds too (task 191b).
 * The alert's ticket lives in bluesphere-ticketing; MDR's own ticket strip
 * went with its ticketing module (task 193).
 */

import { useQuery } from '@tanstack/react-query';

import { ApiError, getAlert } from '@bluesphere/api-client';

import { useAccessToken } from '../auth/useAccessToken';
import { useSelectedTenant } from '../auth/useSelectedTenant';
import { useTenant } from '../auth/useTenant';
import { AlertPanel } from '@bluesphere/mdr-alert-panel';
import { PageNav } from '../components/PageNav';
import { ErrorState, Loading, SelectClientPrompt } from '../components/states';

export function AlertDetail({ alertRef }: { alertRef: string }) {
  const { tenantId } = useSelectedTenant();
  const idToken = useAccessToken();
  const { can } = useTenant();
  // Triage (transitions, comments, titles, enrichment) and response are
  // separate permissions in the Story 22 matrix, held on this Client.
  const canTriage = Boolean(tenantId && can('triage', tenantId));
  const canRespond = Boolean(tenantId && can('respond', tenantId));

  const detailKey = ['alert', tenantId, alertRef];
  const detail = useQuery({
    queryKey: detailKey,
    queryFn: () => getAlert(tenantId as string, alertRef, idToken),
    enabled: Boolean(tenantId && idToken),
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
  });

  if (!tenantId) return <SelectClientPrompt what="Alerts" />;

  if (detail.isPending) {
    return (
      <div className="bs-card">
        <Loading height="h-64" />
      </div>
    );
  }
  if (detail.isError) {
    if (detail.error instanceof ApiError && detail.error.status === 404) {
      return (
        <div className="bs-card p-16 text-center">
          <p className="text-sm font-semibold text-bluesphere-textOnLight">Alert not found</p>
          <p className="mt-1 text-sm text-bluesphere-muted">
            BSM ids are per Client: this one may belong to a different Client than the one selected,
            or the link is wrong.
          </p>
        </div>
      );
    }
    return (
      <ErrorState
        title="Could not load this alert"
        error={detail.error}
        onRetry={() => void detail.refetch()}
      />
    );
  }


  return (
    <div className="space-y-6">
      <PageNav backTo="/alerts" backLabel="Alerts" />
      <AlertPanel
        tenantId={tenantId}
        alertRef={alertRef}
        data={detail.data}
        detailKey={detailKey}
        canTriage={canTriage}
        canRespond={canRespond}
      />
    </div>
  );
}
